/**
 * Workspace Items Hook
 *
 * Transforms tables and linked tables into unified workspace items.
 * Handles filtering by folder, favorites, recent, and trash views.
 *
 * @module features/workspace/hooks/useWorkspaceItems
 */

import { useMemo } from 'react';
import type { ConnectorType } from '@loader/shared-types';

import type { WorkspaceItem } from '../components';

import { useTablesListQuery } from '@/features/tables/hooks/useTableQueries';
import { useLinkedTablesListQuery } from '@/features/linkedTables/hooks/useLinkedTableQueries';
import { useTrashItemsQuery, useFoldersQuery } from '@/features/organization/hooks';
import type { TableStatus, LinkedTableStatus } from '@/lib/types';

// ============================================================================
// TYPES
// ============================================================================

export interface UseWorkspaceItemsOptions {
  /** Filter by folder ID (null = root, undefined = all) */
  folderId?: string | null;
  /** Show only favorites */
  showFavorites?: boolean;
  /** Show only recent items (last 7 days) */
  showRecent?: boolean;
  /** Show trash items */
  showTrash?: boolean;
}

export interface UseWorkspaceItemsResult {
  /** Transformed and filtered workspace items */
  items: WorkspaceItem[];
  /** Loading state */
  isLoading: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const RECENT_DAYS = 7;

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to get workspace items with filtering
 *
 * Transforms tables and linked tables from TanStack Query into unified
 * WorkspaceItem format, applying filters for folder, favorites, recent, etc.
 *
 * When filtering by trash, uses server-side filtering for accuracy.
 *
 * Data flow:
 * 1. TanStack Query hooks fetch tables and linked tables from API
 * 2. Queries are disabled when server provides complete filtered data (trash view)
 * 3. Raw data is transformed into WorkspaceItem[] format
 * 4. Client-side filters applied (folder, favorites, recent)
 */
export function useWorkspaceItems(options: UseWorkspaceItemsOptions = {}): UseWorkspaceItemsResult {
  const { folderId, showFavorites, showRecent, showTrash } = options;

  // Fetch tables and linked tables via TanStack Query
  // Disable when showing trash (server provides complete data)
  const { data: tablesData, isLoading: isTablesLoading } = useTablesListQuery({
    enabled: !showTrash,
  });
  const { data: linkedTablesData, isLoading: isLinkedTablesLoading } = useLinkedTablesListQuery({
    enabled: !showTrash,
  });

  // Extract arrays with defaults (memoized to prevent dependency array issues)
  const tables = useMemo(() => tablesData ?? [], [tablesData]);
  const linkedTables = useMemo(() => linkedTablesData ?? [], [linkedTablesData]);

  const { data: folders, isLoading: isFoldersLoading } = useFoldersQuery({
    enabled: !showTrash,
  });
  const { data: trashItems, isLoading: isTrashLoading } = useTrashItemsQuery({
    enabled: showTrash,
  });

  const items = useMemo((): WorkspaceItem[] => {
    // If showing trash, use trash items with client-side folder filtering
    if (showTrash && trashItems) {
      return transformTrashItems(trashItems, folderId);
    }

    // Transform active items (tables, linked tables, and folders)
    let result = transformActiveItems(tables, linkedTables, folders ?? [], folderId);

    // Apply favorites filter
    if (showFavorites) {
      result = result.filter((item) => item.isFavorite);
    }

    // Apply recent filter
    if (showRecent) {
      result = filterByRecent(result, RECENT_DAYS);
    }

    return result;
  }, [tables, linkedTables, folders, folderId, showFavorites, showRecent, showTrash, trashItems]);

  return {
    items,
    isLoading:
      isTablesLoading ||
      isLinkedTablesLoading ||
      isFoldersLoading ||
      (showTrash ? isTrashLoading : false),
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Transform trash items to workspace items with client-side folder filtering.
 *
 * - No folderId (undefined): show top-level items only (items whose parent/folder is NOT also trashed)
 * - folderId provided: show direct children of that trashed folder
 */
function transformTrashItems(trashItems: {
  folders: Array<{
    id: string;
    name: string;
    parent_folder_id: string | null;
    deleted_at: string;
    updated_at: string;
    created_at: string;
  }>;
  tables: Array<{
    id: string;
    name: string;
    description: string | null;
    is_favorite: boolean;
    folder_id: string | null;
    deleted_at: string;
    updated_at: string;
    created_at: string;
  }>;
  linked_tables: Array<{
    id: string;
    name: string;
    description: string | null;
    is_favorite: boolean;
    folder_id: string | null;
    deleted_at: string;
    updated_at: string;
    created_at: string;
  }>;
}, folderId?: string | null): WorkspaceItem[] {
  const items: WorkspaceItem[] = [];

  // Build a set of trashed folder IDs for quick lookup
  const trashedFolderIds = new Set(trashItems.folders.map((f) => f.id));

  // Filter helper: should this item be shown at the current level?
  const shouldShow = (parentId: string | null): boolean =>
    folderId
      ? parentId === folderId
      : parentId === null || !trashedFolderIds.has(parentId);

  trashItems.folders.forEach((folder) => {
    if (!shouldShow(folder.parent_folder_id)) return;

    items.push({
      id: folder.id,
      type: 'folder',
      name: folder.name,
      isFavorite: false,
      updatedAt: folder.updated_at,
      createdAt: folder.created_at,
      deletedAt: folder.deleted_at,
    });
  });

  trashItems.tables.forEach((table) => {
    if (!shouldShow(table.folder_id)) return;

    items.push({
      id: table.id,
      type: 'table',
      name: table.name,
      description: table.description,
      isFavorite: table.is_favorite,
      updatedAt: table.updated_at,
      createdAt: table.created_at,
      deletedAt: table.deleted_at,
    });
  });

  trashItems.linked_tables.forEach((linkedTable) => {
    if (!shouldShow(linkedTable.folder_id)) return;

    items.push({
      id: linkedTable.id,
      type: 'linked_table',
      name: linkedTable.name,
      description: linkedTable.description,
      isFavorite: linkedTable.is_favorite,
      updatedAt: linkedTable.updated_at,
      createdAt: linkedTable.created_at,
      deletedAt: linkedTable.deleted_at,
    });
  });

  return items;
}

/**
 * Transform active tables, linked tables, and folders to workspace items
 */
function transformActiveItems(
  tables: Array<{
    id: string;
    name: string;
    description: string | null;
    is_favorite?: boolean;
    folder_id?: string | null;
    deleted_at?: string | null;
    updated_at: string;
    created_at: string;
    version_count?: number;
    storage_bytes?: number;
    status?: TableStatus;
    connector_type?: ConnectorType | null;
  }>,
  linkedTables: Array<{
    id: string;
    name: string;
    description: string | null;
    is_favorite?: boolean;
    folder_id?: string | null;
    deleted_at?: string | null;
    updated_at: string;
    created_at: string;
    status?: LinkedTableStatus;
  }>,
  folders: Array<{
    id: string;
    name: string;
    parent_folder_id: string | null;
    deleted_at: string | null;
    updated_at: string;
    created_at: string;
    table_count: number;
    linked_table_count: number;
    subfolder_count: number;
  }>,
  folderId?: string | null
): WorkspaceItem[] {
  const items: WorkspaceItem[] = [];

  // Add subfolders first (folders whose parent matches the current folder)
  folders.forEach((folder) => {
    if (folder.deleted_at) return;
    // For folders, we check parent_folder_id instead of folder_id
    if (!matchesParentFolder(folder.parent_folder_id, folderId)) return;

    items.push({
      id: folder.id,
      type: 'folder',
      name: folder.name,
      isFavorite: false, // Folders don't have favorites yet
      itemCount: folder.table_count + folder.linked_table_count + folder.subfolder_count,
      updatedAt: folder.updated_at,
      createdAt: folder.created_at,
    });
  });

  // Add tables (exclude trashed items)
  tables.forEach((table) => {
    if (table.deleted_at) return;
    if (!matchesFolder(table.folder_id, folderId)) return;

    items.push({
      id: table.id,
      type: 'table',
      name: table.name,
      description: table.description,
      isFavorite: table.is_favorite ?? false,
      versionCount: table.version_count,
      storageBytes: table.storage_bytes,
      updatedAt: table.updated_at,
      createdAt: table.created_at,
      status: table.status,
      connectorType: table.connector_type,
    });
  });

  // Add linked tables (exclude trashed items)
  linkedTables.forEach((linkedTable) => {
    if (linkedTable.deleted_at) return;
    if (!matchesFolder(linkedTable.folder_id, folderId)) return;

    items.push({
      id: linkedTable.id,
      type: 'linked_table',
      name: linkedTable.name,
      description: linkedTable.description,
      isFavorite: linkedTable.is_favorite ?? false,
      updatedAt: linkedTable.updated_at,
      createdAt: linkedTable.created_at,
      status: linkedTable.status,
    });
  });

  return items;
}

/**
 * Check if item folder matches the filter
 */
function matchesFolder(
  itemFolderId: string | null | undefined,
  filterFolderId: string | null | undefined
): boolean {
  // No filter - show all
  if (filterFolderId === undefined) return true;
  // Filter for root - item must have no folder
  if (filterFolderId === null) return itemFolderId === null || itemFolderId === undefined;
  // Filter for specific folder
  return itemFolderId === filterFolderId;
}

/**
 * Check if folder's parent matches the filter (for showing subfolders)
 */
function matchesParentFolder(
  parentFolderId: string | null | undefined,
  filterFolderId: string | null | undefined
): boolean {
  // No filter (undefined) - show all root folders only (for "All Items" view we don't want all folders)
  if (filterFolderId === undefined) return parentFolderId === null || parentFolderId === undefined;
  // Filter for root - show folders with no parent
  if (filterFolderId === null) return parentFolderId === null || parentFolderId === undefined;
  // Filter for specific folder - show folders whose parent is that folder
  return parentFolderId === filterFolderId;
}

/**
 * Filter items by recent activity
 */
function filterByRecent(items: WorkspaceItem[], days: number): WorkspaceItem[] {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);

  return items.filter((item) => {
    const updatedAt = item.updatedAt ? new Date(item.updatedAt) : null;
    return updatedAt && updatedAt >= cutoffDate;
  });
}
